import React, { Component } from 'react';
import PropTypes from 'prop-types';
import simpleheat from 'simpleheat';

import widgetWrapper from './widgetWrapper';

import { formatTimestamp } from './widgetUtils';

import './HeatMapWidget.scss';

import basemap from './heatmap-baseimage.png';

const CANVAS_WIDTH = 400;
const CANVAS_HEIGHT = 240;

const NORTH_LATITUDE = 50;
const SOUTH_LATITUDE = 26;
const WEST_LONGITUDE = -126;
const EAST_LONGITUDE = -70;

const RADIUS = 14;
const BLUR = 20;
const CELL_SIZE = 4;

const ATTENUATION = 0.01;

class HeatMapWidget extends Component {

  static propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    details: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    map: PropTypes.shape({ markers: PropTypes.array }).isRequired,
  };

  static defaultProps = {
    title: 'MAP',
    subtitle: '',
    details: '',
    timestamp: '',
  };

  constructor(...args) {
    super(...args);
    this.canvasRef = React.createRef();
  }

  componentDidMount() {
    this.doCanvasRendering();
  }

  componentDidUpdate(_prevProps, _prevState) {
    this.doCanvasRendering();
  }

  doCanvasRendering() {
    if (this.props.map) {

      const { markers = [] } = this.props.map;

      const canvas = this.canvasRef.current;
      if (canvas) {

        const context = canvas.getContext('2d');
        const baseImage = new Image();
        baseImage.src = basemap;
        baseImage.onload = () => {

          const baseImageOffset = (latitude, longitude) => ({
            x: baseImage.width + 512 * (longitude / 180),
            y: baseImage.height - 145 * Math.tan(2 * Math.PI * (latitude / 360)), // Experimented, got 145
          });

          let offscreenCanvas = document.createElement('canvas');
          offscreenCanvas.width = baseImage.width;
          offscreenCanvas.height = baseImage.height;
          const heat = simpleheat(offscreenCanvas);
          const rawData = markers.map(([lat, lng, weight]) => {
            const loc = baseImageOffset(lat, lng);
            return ([loc.x, loc.y, weight]);
          });

          const cellSize = CELL_SIZE; // radius / 2;
          const grid = [];
          rawData.forEach(([x, y, weight]) => {
            const k = ATTENUATION * weight;
            const cellX = Math.floor(x / cellSize);
            const cellY = Math.floor(y / cellSize);
            grid[cellY] = grid[cellY] || [];
            const cell = grid[cellY][cellX];
            if (!cell) {
              grid[cellY][cellX] = [cellX, cellY, k];
            } else {
              cell[0] = (cell[0] * cell[2] + x * k) / (cell[2] + k); // x
              cell[1] = (cell[1] * cell[2] + y * k) / (cell[2] + k); // y
              cell[2] += k; // cumulated intensity value
            }
          });

          const data = [];
          for (let i = 0; i < grid.length; i++) {
            if (grid[i]) {
              for (let j = 0; j < grid[i].length; j++) {
                const cell = grid[i][j];
                if (cell) {
                  data.push([
                    Math.round(cellSize * cell[0]),
                    Math.round(cellSize * cell[1]),
                    Math.min(1, cell[2]),
                  ]);
                }
              }
            }
          }
          heat.data(data);
          // heat.gradient({ 0.0: 'black', 0.1: 'green', 0.4: 'blue', 0.65: 'lime', 1: 'red' });
          heat.radius(RADIUS, BLUR);
          heat.draw(0.4); // minopacity

          const northwest = baseImageOffset(NORTH_LATITUDE, WEST_LONGITUDE);
          const southeast = baseImageOffset(SOUTH_LATITUDE, EAST_LONGITUDE);
          const sourceWidth = southeast.x - northwest.x;
          const sourceHeight = southeast.y - northwest.y;
          context.drawImage(baseImage,
            northwest.x, northwest.y, sourceWidth, sourceHeight,
            0, 0, canvas.width, canvas.height);
          context.drawImage(offscreenCanvas,
            northwest.x, northwest.y, sourceWidth, sourceHeight,
            0, 0, canvas.width, canvas.height);
          offscreenCanvas = null;
        };
      }
    }
  }

  render() {

    const {
      title = 'MAP',
      subtitle = '',
      details = '',
      timestamp = '',
    } = this.props;

    return (
      <div className="HeatMapWidget">
        <div className="header">
          <div className="title">{title}</div>
          {
            subtitle ? <div className="subtitle">{subtitle}</div> : ''
          }
        </div>
        <div className="content">
          <canvas width={CANVAS_WIDTH} height={CANVAS_HEIGHT} ref={this.canvasRef} />
        </div>
        <div className="footer">
          <div className="details">{details}</div>
          <div className="timestamp">{formatTimestamp(timestamp)}</div>
        </div>
      </div>
    );
  }
}

export default widgetWrapper(HeatMapWidget);
